import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath, Redirect, Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import Banner from '@cpg-its/ads.containers.banner';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { ContentSpacer } from '../../../../TCSMApp/global/components/layout/generic-content-spacer';
import { GetDetail, Search } from '../../ducks/parish-api';
import ParishDetail from './parish-detail';
import { Parish, ParishComplete, ParishFilters, ParishQueryStringParameters, ParishSearchSort } from './parish-model';
import ParishResults from './parish-results';
import ParishSearch from './parish-search';
import { apiErrorClear } from '../../../../TCSMApp/global/ducks/base-api';
import AppMail from '../mail/app-mail';
import { MailDestination } from '../mail/mail-model';
import { EcdRoutes } from '../../constants/routes';
import { EcdStates } from '../../../root/ecd-model';
import { CleanURLSearchParams, getBasePath } from '../../helpers/routing';
import { useQuery } from '../../../../TCSMApp/global/hooks/useQuery';
import { enumFromValue } from '../../../../TCSMApp/global/helpers/format-helpers';

function AppParish(){    
    const dispatch = useDispatch();    
    const history = useHistory();
    const routeMatch = useRouteMatch();
    const searchParams = new URLSearchParams(useQuery());

    const location = useLocation()?.pathname;    
    const queryString = useLocation()?.search;    

    const matchResults = matchPath(location, {
        path: `${routeMatch.path}/${EcdRoutes.Results}`,
        exact: true,
        strict: false,
      });

    const currentlanguage = useSelector<EcdStates, string>(state => state?.globalization?.currentlanguage);    
    const basePath = getBasePath(currentlanguage);
    
    const initialResults: Parish[] = [];

    const queryParamsFilters: ParishFilters = {
        name: searchParams?.get(ParishQueryStringParameters.Name),
        type: searchParams?.get(ParishQueryStringParameters.Type),        
        zip_code: searchParams?.get(ParishQueryStringParameters.ZipCode),
        location: searchParams?.get(ParishQueryStringParameters.Location),        
        sort: enumFromValue(searchParams?.get(ParishQueryStringParameters.Sort), ParishSearchSort),              
        page: searchParams?.get(ParishQueryStringParameters.Page),        
    };

    const initialFilters: ParishFilters = {};
    const initialParishDetail: ParishComplete = {
        id: '',
        name: '',
    };
    
    const initialMailDestination: MailDestination = {
        name: '',
        email: '',
        itemGuid:'',
    };
    
    const [contextParishDetail, setContextParishDetail] = useState(initialParishDetail);
    const [searchResults, setSearchResults] = useState(initialResults);
    const [contextFilters, setContextFilters] = useState(initialFilters);        
    const [errorMessage, setErrorMessage] = useState<string>(null);    
    const [contextMailDestination, setContextMailDestination] = useState(initialMailDestination);
    const [searchInProgress, setSearchInProgress] = useState(false);

    // Detail Search ------------------------
    function OnDetailSuccess(parishId: string | number, serverResult, isReplace: boolean){
        if (serverResult?.status?.code === 'SUCCESS') {
            setContextParishDetail(
                {
                    ...serverResult?.data,
                    id: parishId,
                }); 

                if (isReplace){
                    history.replace(`${routeMatch.path}/${parishId}/${EcdRoutes.Detail}`);
                    return;
                }                

                history.push(`${routeMatch.path}/${parishId}/${EcdRoutes.Detail}`);
        }
        
        if (serverResult?.status?.code === 'ERROR') {            
            setErrorMessage(serverResult?.status?.message);
        }
    }   
    
    function OnDetailMailSuccess(parishId: string | number, serverResult){
        if (serverResult?.status?.code === 'SUCCESS') {
            setContextParishDetail(serverResult?.data);            

            setContextMailDestination(
                {
                    email: serverResult?.data?.email,
                    name: serverResult?.data?.name,
                    itemGuid: parishId?.toString(),
                });        
            history.replace(`${routeMatch.path}/${parishId}/${EcdRoutes.Detail}/${EcdRoutes.Mail}`);        
        }
        
        if (serverResult?.status?.code === 'ERROR') {            
            setErrorMessage(serverResult?.status?.message);
        }
    }   
    
    function SelectParishById(parishId: string | number, isReplace: boolean = false){
        if (!parishId) return;
        
        dispatch(GetDetail(parishId, (serverResult)=> OnDetailSuccess(parishId, serverResult, isReplace)));
    }    
    // -------------------------------------

    // Search ------------------------------
    function OnSearchSucess(filters: ParishFilters, serverResult, isReplace: boolean = false){
        if (serverResult?.status?.code === 'SUCCESS') {
            setSearchResults(serverResult?.data);                        

            const params = new URLSearchParams(
                {
                    ...filters,                    
                });

            CleanURLSearchParams(params);

            const contextCompare = new URLSearchParams(
                {
                    ...contextFilters,                    
                });
    
            CleanURLSearchParams(contextCompare);                
            
            const contextCompareString = contextCompare?.toString();
            const paramsString = params?.toString();

            // The url is the same, no need to update.
            if (contextCompareString === paramsString) return null;

            if (isReplace){
                history.replace({
                    pathname: `${routeMatch.path}/${EcdRoutes.Results}`,
                    search: params.toString(),
                });                
            }  else {
                history.push({
                    pathname: `${routeMatch.path}/${EcdRoutes.Results}`,
                    search: params.toString(),
                });
            }    
        }
        
        if (serverResult?.status?.code === 'ERROR') {            
            setErrorMessage(serverResult?.status?.message);
        }         
        
        setSearchInProgress(false);
    }    
    
    function PerformSearch(filters: ParishFilters, isReplace: boolean = false){
        if (!filters) return;

        setSearchInProgress(true);
        setSearchResults(initialResults);
        setContextFilters(filters);
        dispatch(apiErrorClear());
        dispatch(Search(filters, (serverResult)=> OnSearchSucess(filters, serverResult, isReplace)));
    }

    function SetSort(searchFilters: ParishFilters, selectedSort : ParishSearchSort){

        const newSort: ParishSearchSort = selectedSort || ParishSearchSort.NameAsc;

        const params = new URLSearchParams(
            {
                ...searchFilters,
                sort: newSort,
            });

        CleanURLSearchParams(params);

        history.push({
            pathname: `${routeMatch.path}/${EcdRoutes.Results}`,
            search: params.toString(),
        });        
    }
    // -------------------------------------

    // Clergy Detail Search ------------------------
    function SelectClergyById(clergyId: string | number){
        if (!clergyId) return;
        
        history.push(`${basePath}/${EcdRoutes.Clergy}/${clergyId}/${EcdRoutes.Detail}`);        
    }    
    // -------------------------------------

    // Navigate ----------------------------
    function NavigateBack(){               
        history.goBack();
    }

    function NavigateNewSearch(){
        setSearchResults(initialResults);
        setContextFilters(initialFilters);        
        setContextParishDetail(initialParishDetail);        
        
        history.push(`${routeMatch.path}/${EcdRoutes.Search}`);
    }

    function NavigateToPageIndex(newPageIndex: number){
        // Created this if in the future the pagination 
        // is going to be done on the server.        
        
        const params = new URLSearchParams(
            {
                ...contextFilters,
                page: newPageIndex?.toString(),
            });

        CleanURLSearchParams(params);

        history.push({
            pathname: `${routeMatch.path}/${EcdRoutes.Results}`,
            search: params.toString(),
        });         
    }

    function NavigateToSendEmail(parishId: string | number, destination: MailDestination){
        if (!parishId && !destination?.email) return;

        if (parishId && !destination?.email){
            dispatch(GetDetail(parishId, (serverResult)=> { OnDetailMailSuccess(parishId, serverResult); }));
            return;
        }

        setContextMailDestination(destination);        
        history.push(`${routeMatch.path}/${parishId}/${EcdRoutes.Detail}/${EcdRoutes.Mail}`);                
    }
    // -------------------------------------    

    function OnClearForm(){
        setContextFilters(initialFilters);
    }

    function SearchError(){
        if (!errorMessage) return null;

        return (<>
            <ContentSpacer>
                <Banner
                    banner={{
                        type: 'error',
                        label: <p><strong>{errorMessage}</strong></p>,
                    }}
                />
            </ContentSpacer>
        </>);
    }

    useEffect(() => {
        if (!matchResults) return;

        const contextCompare = new URLSearchParams(
            {
                ...contextFilters,
                page: '1',
            });

        const queryCompare = new URLSearchParams(
            {
                ...queryParamsFilters,
                page: '1',
            });

        CleanURLSearchParams(contextCompare);
        CleanURLSearchParams(queryCompare);

        // Only perform search when the paramters change.
        const contextCompareString = contextCompare?.toString();
        const queryCompareString = queryCompare?.toString();

        if (contextCompareString !== queryCompareString &&
            (queryParamsFilters.name ||
            queryParamsFilters.type ||            
            queryParamsFilters.location ||
            queryParamsFilters.zip_code ||            
            queryParamsFilters.sort ||
            queryParamsFilters.page)){
            PerformSearch(queryParamsFilters, true);            
        }        

        if (contextCompareString === queryCompareString && contextFilters?.page !== queryParamsFilters?.page){
            setContextFilters({
                ...contextFilters,
                page: queryParamsFilters?.page,
            });
        }

	}, [location, queryString]);

    return (        
        <>     
            <SearchError />            
            <Suspense fallback={<LoadingCircle />}>
                <Switch>                
                    <Route exact path={`${routeMatch.path}`}>
                        <Redirect to={{ pathname: `${routeMatch.path}/${EcdRoutes.Search}/` }}/>
                    </Route>
                    <Route exact path={`${routeMatch.path}/${EcdRoutes.Search}`}>
                        <ParishSearch performSearch={PerformSearch} selectParishById={SelectParishById} 
                            searchFilters={contextFilters} clearForm={OnClearForm} />
                    </Route>                                        
                    <Route exact path={`${routeMatch.path}/${EcdRoutes.Results}`}>
                        { contextFilters ? 
                            <ParishResults searchResults={searchResults} searchFilters={contextFilters} 
                            onNavigateBack={NavigateBack} onNewSearch={NavigateNewSearch} setSort={SetSort} 
                            currentPageIndex={contextFilters?.page ? Number(contextFilters?.page) : 1} navigateToPageIndex={NavigateToPageIndex}
                            selectParishById={SelectParishById} searchInProgress={searchInProgress}  />
                            : <LoadingCircle />}                        
                    </Route>                      
                    <Route exact path={`${routeMatch.path}/:parishid/${EcdRoutes.Detail}`}>                        
                        <ParishDetail selectedParish={contextParishDetail} onNavigateBack={NavigateBack} 
                                onNewSearch={NavigateNewSearch} onSelectClergyById={SelectClergyById}
                                selectParishById={SelectParishById} onNavigateToSendEmail={NavigateToSendEmail} />
                    </Route> 
                    <Route exact path={`${routeMatch.path}/:itemId/${EcdRoutes.Detail}/${EcdRoutes.Mail}`}>
                        <AppMail onNavigateBack={NavigateBack} destination={contextMailDestination} onNavigateToSendEmail={NavigateToSendEmail} />
                    </Route>                  
                    <Route>                                                
                        <Redirect to={{ pathname: `${routeMatch.path}/${EcdRoutes.Search}/` }}/>
                    </Route>
                </Switch>
            </Suspense> 
            <SearchError />                              
        </>        
    );
}

export default AppParish;
